// Split out of `../runner.ts`, which is now the barrel over this directory.

// ─── the one seam: can this box unwrap a sealed credential? (#1505) ───────────
//
// Every credential the mothership seals onto an assignment is wrapped with the
// box's OWN subkey (`deriveBoxKekRing(kek, runnerId)` — #1300 stopped the master
// key leaving the platform), and the box unwraps it with the `DZ_BOX_KEK_BASE64`
// its enrollment handed over. A binary that predates that exchange looks for the
// platform master key instead, finds nothing, and DIES at the config seam —
// `required secret missing: KEK_BASE64` — one second into the run, before any
// brain starts. It does not degrade to a floor result; the task just fails.
//
// So this is a real capability, and no box advertises it: `runners.capabilities` is
// written ONLY by setup mode's `capabilities_detected` advert
// (`apps/worker/src/fleet/capabilities-ingest.ts`), every live box's advert is a
// bare `{}`, and the presence beat carries no capability field at all
// (`RunnerPresenceSchema`). It is inferred instead, from the build the box reports
// on every beat.
//
// **But "the box CAN'T tell us" was never the real argument, and an earlier draft
// made it wrongly** — it claimed the boxes in question are `worker`s that never run
// setup, which is backwards: the three INCAPABLE boxes are `universal`, the role
// that CAN take a setup assignment, and the one CAPABLE box is the `worker`. The
// argument that actually holds is stronger: the platform must not TRUST a box's
// self-report for this token, since `capabilities` is an untrusted box's own write
// over an open vocabulary with no removal path. That is why the token is
// platform-reserved at READ — dropped from `stored` in {@link effectiveProvisions},
// case-insensitively. The matching write-seam refusal in {@link mergeProvisions} was
// built and then deliberately CUT (see the docstring below, and #1510); an earlier
// draft of this header claimed both sides, which the same file then contradicted 120
// lines down. One filter guards this today, and a reader planning to remove it must
// not believe a second one is standing behind it.
//
// **Be precise about what that inference is worth — it is a FAIL-CLOSED PROXY, in
// both directions, and every earlier draft of this comment overstated it.**
//
//   - It is NECESSARY, not sufficient. `DZ_BOX_KEK_BASE64` is written by the JOIN
//     script from the enroll response, and a self-update swaps the box's BINARY,
//     not its `/etc/dz-runner/env` — so a box enrolled before #1307 and rolled
//     forward reports a current version, holds no subkey, and dies at the very same
//     config seam while this predicate calls it capable. That population is exactly
//     what #1385 (re-enroll the fleet onto per-box KEKs) exists to drain. This fence
//     makes the pre-cutoff failure IMPOSSIBLE; it does not make the failure
//     impossible.
//   - The version is BOX-REPORTED, not platform-observed. It rides the presence
//     beat from the CI build stamp (`DZ_RUNNER_VERSION` was removed as a version
//     source in #1843, so an operator can no longer pin it — a modified binary
//     still can). A forged HIGH value defeats the fence outright, and the cost is
//     not confined to the forger:
//     a `user`/`shared` box draws from every org in its claim scope, so it claims
//     review tasks it cannot run and fails them terminally, DENYING those accounts
//     their review. (An earlier draft said such a box "earns nothing but its own
//     failed run". That was wrong in both halves.)
//   - `0.0.1` is ALSO `FALLBACK_RUNNER_VERSION` — what any UNSTAMPED binary reports,
//     including a source/Docker run of today's code. Such a box is fenced out of the
//     review lane despite being perfectly capable. That is the safe direction, and
//     it is the reason the escalation sweep matters.
//   - **A DOWNGRADE only withdraws the token if the box keeps REPORTING one.** The
//     reaper's advert sync treats a null version as "no signal, never a false write"
//     (`apps/worker/src/cron/runner-sweep.ts`), so a rollback to a binary whose
//     presence payload omits `version` — or fails `RunnerPresenceSchema` — leaves
//     `runners.runner_version` frozen at the last value it did report. Re-deriving
//     every pass withdraws the token as soon as the box reports something LOWER; it
//     cannot withdraw it from a box that has stopped reporting at all. Freshness is
//     a property of the column, and this predicate does not own it.
//   - **Withdrawal LAGS a downgrade by up to one sweep cadence, and the sentence
//     above hides that if read alone.** "As soon as the box reports something LOWER"
//     tracks the COLUMN, and the column trails the box: a downgrade IN PLACE (a
//     self-update swap plus a restart, or `runner.update_rolled_back`) re-puts the
//     presence key inside its TTL, so the box never goes offline, is never reaped,
//     and never takes the revive path's atomic version carry. The row stays `online`
//     at the stale-HIGH value until the next advert-sync tick — a 2-minute sweep
//     against a 15-second assigner, so roughly 8 claim passes during which the token
//     is granted to a binary that cannot unwrap the seal. The offline->online door
//     is closed (#1505); THIS one is not, and closing it needs a freshness signal
//     the predicate can read — an observed-at column, or grading the live presence
//     advert at claim time — which is #1511's scope, not this function's.
//
// What the predicate does prove is the negative, which is the half #1505 needed: a
// binary older than the cutoff CANNOT open a per-box seal, whatever else is true.
//
// Derived, never PERSISTED — that distinction is load-bearing. `capabilities` is
// an additive, single-writer column ("absence is not evidence"), so a token
// written there could never be withdrawn when a box downgrades. Overlaying at
// READ ({@link effectiveProvisions}) re-decides it every pass, keeps
// `capabilities-ingest.ts` the column's only writer, and leaves exactly one place
// where "can this box unwrap a sealed credential" is answered.

/**
 * The first `dz-runner` release that unwraps an assignment's sealed credentials
 * with its own per-box subkey. `DZ_BOX_KEK_BASE64` reached `apps/runner` in
 * #1307 (`e95bfdc1`, 2026-08-03), which landed AFTER the `0.0.3` version bump
 * (`f7a455b5`) and shipped in `0.0.4` (`9e404085`) — so `0.0.3` is ambiguous and
 * deliberately excluded rather than rounded down.
 */
export const RUNNER_SEALED_SECRETS_MIN_VERSION = '0.0.4';

/**
 * Parse the `MAJOR.MINOR.PATCH` core of a runner build string. Build metadata is
 * stripped (a released box reports `0.0.6+sha-a45d7e0`); ANYTHING else — a
 * prerelease tag, a two-part version, a `v` prefix, junk — is `null`, which the
 * caller reads as "not capable". A version we cannot compare must never grant a
 * gate, and a canary prerelease that loses review work says so loudly through the
 * capability-escalation sweep rather than failing at the box's config seam.
 *
 * **The whole string is matched, not just its head.** An earlier draft split on
 * `+` and digit-tested the parts, which ACCEPTED `0.0.4+` (empty build metadata),
 * `0.0.4+foo+bar` (two `+`), `00.00.04` (leading zeros), and a component long
 * enough to overflow to `Infinity` — each of which then compared as CAPABLE while
 * this comment claimed everything unrecognized was null. Caught by CodeRabbit;
 * both deep-review lenses had probed this function and missed it.
 */
const VERSION_CORE = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:\+[0-9A-Za-z.-]+)?$/;

function parseVersionCore(raw: string): [number, number, number] | null {
  const m = VERSION_CORE.exec(raw.trim());
  if (m === null) return null;
  const nums = [Number(m[1]), Number(m[2]), Number(m[3])];
  // A component may still be long enough to lose precision or overflow; an
  // uncomparable number must not grant the gate.
  if (!nums.every((n) => Number.isSafeInteger(n))) return null;
  return [nums[0] as number, nums[1] as number, nums[2] as number];
}

/**
 * Does the build this box reports (`runners.runner_version`, synced from its
 * presence beat by the reaper) carry the per-box vault subkey? Numeric
 * field-by-field compare against {@link RUNNER_SEALED_SECRETS_MIN_VERSION} — a
 * STRING compare would place `0.0.10` below `0.0.6`. Fail-closed on every
 * unknown: null, blank, or unparseable ⇒ `false`. Pure.
 */
export function boxKekCapable(runnerVersion: string | null | undefined): boolean {
  return runnerVersionAtLeast(runnerVersion, RUNNER_SEALED_SECRETS_MIN_VERSION);
}

/**
 * The ONE version gate every build-inferred capability reads. Numeric
 * field-by-field compare (a STRING compare would place `0.0.10` below `0.0.6`);
 * fail-closed on every unknown — null, blank, or unparseable ⇒ `false`. Private:
 * a capability gets a named predicate and a named cutoff, never a bare call.
 */
function runnerVersionAtLeast(runnerVersion: string | null | undefined, minimum: string): boolean {
  if (!runnerVersion) return false;
  const version = parseVersionCore(runnerVersion);
  const min = parseVersionCore(minimum);
  if (version === null || min === null) return false;
  for (let i = 0; i < 3; i += 1) {
    if (version[i] !== min[i]) return (version[i] as number) > (min[i] as number);
  }
  return true;
}

// ─── can this box APPLY an agent mode's tool narrowing? (plan 101 slice 06) ────
//
// The fleet assignment is deliberately non-strict, so a box built before
// `agent_mode` existed STRIPS the field and runs with every tool — a narrowing the
// dashboard says is on, silently not applied. So a narrowed mode is only shipped to
// a box whose build applies it, inferred from the build it reports exactly like
// {@link boxKekCapable}, and with the same caveats (a box-reported version is a
// fail-closed proxy; withdrawal on downgrade lags by one advert sync).

/** The first `dz-runner` release that applies `agent_mode` narrowing and echoes it on `prompt_composed`. */
export const RUNNER_MODE_NARROWING_MIN_VERSION = '0.0.14';

/** Does the build this box reports apply an assignment's `agent_mode` narrowing? Pure, fail-closed. */
export function boxModeNarrowingCapable(runnerVersion: string | null | undefined): boolean {
  return runnerVersionAtLeast(runnerVersion, RUNNER_MODE_NARROWING_MIN_VERSION);
}

/**
 * The first `dz-runner` release whose team lane locks its planned set (`files_plan`) and
 * gives an `assignment.plan_admitted` claim back — never edits — when that lock is held or
 * unanswered, or when the claim reached a lane that takes no plan lock (#5369, #5370).
 */
export const RUNNER_PLAN_ADMISSION_MIN_VERSION = '0.0.27';

/** May the build this box reports be handed a lock-free planning claim? Pure, fail-closed. */
export function boxPlanAdmissionCapable(runnerVersion: string | null | undefined): boolean {
  return runnerVersionAtLeast(runnerVersion, RUNNER_PLAN_ADMISSION_MIN_VERSION);
}

/**
 * The first `dz-runner` release that answers a MODEL-CALL OFFER (#6760, epic #6552): it
 * routes a `triage_judge` / `kb_fold` assignment to the model-call executor, fetches the
 * prompt over `model_call_prompt`, makes the one call on the sealed credential bound to the
 * job's `model_pin` and posts it back over `model_call_result`.
 *
 * ONE PATCH ABOVE the package version at this slice's base (`0.1.21`, after #7486), and on
 * purpose: a runner version bump is its own release PR, so no build of the slice that adds
 * this constant carries it, and a canary of that slice alone is never offered a model call.
 * The release that carries the executor AND #6761's deadline rule is the one that reaches
 * it; #6761 raises it if a bump lands in between (#6760 section 9).
 */
export const RUNNER_MODEL_CALL_MIN_VERSION = '0.1.22';

/**
 * The first `dz-runner` release that renders a maintenance recipe (#7802): 0.1.34 is the build
 * before this field, and the release that carries it is the next `/ship`. Until a box reports it,
 * a `review_only` or `issues_only` pass waits `pending` rather than proposing the wrong file.
 */
export const RUNNER_SETUP_RECIPES_MIN_VERSION = '0.1.35';

/** Does the build this box reports propose the recipe a person chose? Pure, fail-closed. */
export function boxSetupRecipesCapable(runnerVersion: string | null | undefined): boolean {
  return runnerVersionAtLeast(runnerVersion, RUNNER_SETUP_RECIPES_MIN_VERSION);
}

/** Does the build this box reports answer a model-call offer? Pure, fail-closed. */
export function boxModelCallCapable(runnerVersion: string | null | undefined): boolean {
  return runnerVersionAtLeast(runnerVersion, RUNNER_MODEL_CALL_MIN_VERSION);
}

/**
 * The first `dz-runner` release that may be offered a PLANNER job (#6765, epic #6552): its
 * build registers the `scout_plan` model-call schema (`@developerz/agent`
 * `model-call/schemas.ts`), and its scout runs the Planner split (the decision call, the
 * `scout_doable` hand-off, and no fallback plan).
 *
 * ONE PATCH ABOVE the higher of `apps/runner/package.json` at #6765's merge base and every
 * other `RUNNER_*_MIN_VERSION` in this file, so no build published before #6765 merges reads
 * as Planner-capable. #6765 bumps the runner to exactly this value, so its own canary build
 * is the first at or above it. Above {@link RUNNER_MODEL_CALL_MIN_VERSION}, so
 * {@link boxPlannerCapable} implies {@link boxModelCallCapable}. Recomputed on every rebase
 * of the slice that sets it (#6765's pre-merge check).
 */
export const RUNNER_PLANNER_MIN_VERSION = '0.1.33';

/** May the build this box reports be offered a Planner job? Pure, fail-closed. */
export function boxPlannerCapable(runnerVersion: string | null | undefined): boolean {
  return runnerVersionAtLeast(runnerVersion, RUNNER_PLANNER_MIN_VERSION);
}

// ─── may this box get a sealed credential on its coding lanes? (plan 102 slice 07) ─
//
// Two builds matter. Before `0.0.17` the coding, babysit and devops lanes built their
// model from the box env alone and ignored any `llm_credential` the assignment carried,
// so a credential sent there would record a pool key on `task.assigned` while the box
// billed its own env. `0.0.17` drives the credential (`laneModelFor`, #4697), but its
// error events do not say WHICH key failed (`ErrorPayloadSchema.key_id` arrives in
// `0.0.18`). Without that, a pool key's 429 reads as the account's backend failing and
// pauses it. So the cutoff is `0.0.18`, and an older box keeps the box-env run it always
// had. Same inference and caveats as {@link boxKekCapable} (a box-reported version is a
// fail-closed proxy; withdrawal on downgrade lags by one advert sync). A predicate, not
// a provision token: no gate matches a whole box on it, only the claim that decides
// whether to seal (`ORIGIN_CREDENTIAL_NEEDS_CODING_RUNNER` names the lanes it applies to).

/** The first `dz-runner` release that drives a coding lane on the sealed credential AND names the key on an error event. */
export const RUNNER_CODING_CREDENTIAL_MIN_VERSION = '0.0.18';

/**
 * May the build this box reports receive a sealed credential on a coding, babysit or
 * devops lane? Pure, fail-closed: null, blank or unparseable ⇒ `false`. Implies
 * {@link boxKekCapable}, since the cutoff is above the per-box KEK's.
 */
export function runnerSealsCodingCredential(runnerVersion: string | null | undefined): boolean {
  return runnerVersionAtLeast(runnerVersion, RUNNER_CODING_CREDENTIAL_MIN_VERSION);
}
